import { useEffect, useState } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";

const DEAL_TEMPLATES = {
  shareholder: "Thank you for writing. If you hold shares and are considering a partial sale, we will need to review your stock purchase agreement and obtain issuer confirmation before any price is discussed. Reply here and we will guide you through it.",
  broker: "Thank you for the introduction. We buy part of a position, not all of it, and we read the stock purchase agreement before any price is discussed. If your seller is open to that, tell us the size and we will respond directly.",
  fund: "Thank you for reaching out. We buy direct positions in named late stage companies and can move quickly once documents are signed. Share the size you are selling down and we will take it from there.",
  general: "Thank you for sending this over. A member of the team will review the position and follow up with you directly.",
};

const APP_TEMPLATES = {
  welcome: "Thank you for applying to Solera. We would like to take this further. A member of the team will be in touch to complete your verification and set up your access.",
  more_info: "Thank you for your interest in Solera. Before we proceed, could you share a little more about your background and what you are looking to buy or sell?",
  decline: "Thank you for your interest in Solera. Membership is by referral and highly selective, and we are not able to extend an invitation at this time. We appreciate you reaching out.",
};

const PARTY_LABEL = { shareholder: "Shareholder", broker: "Broker / Platform", fund: "Fund" };
const TYPE_LABEL = { buyer: "Buyer", seller: "Seller", both: "Buyer & Seller" };

const linkedinHref = (v) => {
  const s = (v || "").trim();
  if (!s) return "";
  const withProto = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  try {
    const host = new URL(withProto).hostname.toLowerCase();
    if (host === "linkedin.com" || host.endsWith(".linkedin.com")) return new URL(withProto).href;
  } catch (e) { return ""; }
  return "";
};
const ts = (v) => (v ? `${v.slice(0, 16).replace("T", " ")} UTC` : "");

export function Inbox() {
  const [deals, setDeals] = useState([]);
  const [apps, setApps] = useState([]);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/deal-inquiries").then((r) => setDeals(r.data)).catch(() => {});
    api.get("/admin/membership-applications").then((r) => setApps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleReply = (key) => { setReplyFor(replyFor === key ? null : key); setReplyText(""); };

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const resolveDeal = (d, status) => act(() => api.post(`/admin/deal-inquiries/${d.inquiry_id}/status`, { status }));
  const replyDeal = async (d) => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/deal-inquiries/${d.inquiry_id}/reply`, { message: replyText });
      toast.success("Reply sent");
      setReplyFor(null); setReplyText(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const reviewApp = (a) => act(() => api.post(`/admin/membership-applications/${a.application_id}/review`));
  const inviteApp = (a) => act(async () => {
    await api.post(`/admin/membership-applications/${a.application_id}/invite`);
    toast.success(`Invite sent to ${a.email}`);
  });
  const replyApp = async (a) => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/membership-applications/${a.application_id}/reply`, { message: replyText });
      toast.success("Reply sent");
      setReplyFor(null); setReplyText(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-16" data-testid="admin-inbox">
      <section data-testid="inbox-deals">
        <div className="flex items-baseline gap-4">
          <span className="t-label">Deal submissions</span>
          <span className="t-cap text-muted-s fig">{deals.length}</span>
        </div>
        <div className="max-w-3xl space-y-0 mt-4">
          {deals.map((d) => {
            const key = `deal:${d.inquiry_id}`;
            return (
              <div key={d.inquiry_id} className="hairline py-5" data-testid={`deal-inquiry-${d.inquiry_id}`}>
                <div className="flex gap-6 items-baseline flex-wrap">
                  <span className="t-body text-bone">{d.company}</span>
                  {d.party && <span className="t-label" data-testid={`deal-party-${d.inquiry_id}`}>{PARTY_LABEL[d.party] || d.party}</span>}
                  <span className="t-cap text-muted-s fig">{ts(d.created_at)}</span>
                  {d.replied && <span className="t-label text-tint" data-testid={`deal-replied-${d.inquiry_id}`}>Replied</span>}
                  {d.resolved && <span className="t-label text-tint" data-testid={`deal-resolved-${d.inquiry_id}`}>Resolved</span>}
                </div>
                <div className="flex gap-6 items-baseline flex-wrap mt-2">
                  <span className="t-small text-bone">{d.name}</span>
                  <a href={`mailto:${d.email}`} className="t-cap text-muted-s hover:!text-[#E8E6E1]">{d.email}</a>
                  {d.deal_size && <span className="t-cap text-muted-s">Size: {d.deal_size}</span>}
                  {d.price && <span className="t-cap text-muted-s">Price: {d.price}</span>}
                </div>
                {d.message && <p className="t-small text-muted-s mt-2 measure">{d.message}</p>}
                <div className="flex gap-5 mt-3 items-center flex-wrap">
                  <button onClick={() => toggleReply(key)} className="t-label hover:!text-[#E8E6E1]" data-testid={`deal-reply-toggle-${d.inquiry_id}`}>{replyFor === key ? "Cancel" : "Reply"}</button>
                  <div className="flex items-center gap-2">
                    <span className="t-cap text-muted-s">Status</span>
                    <select className="input-solera !py-1 !px-2 !text-xs" value={d.status || (d.resolved ? "closed" : "received")}
                      onChange={(e) => resolveDeal(d, e.target.value)} data-testid={`deal-status-${d.inquiry_id}`}>
                      <option value="received">Received</option>
                      <option value="reviewing">Under review</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
                {replyFor === key && (
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2 flex-wrap" data-testid={`deal-reply-templates-${d.inquiry_id}`}>
                      {Object.entries(DEAL_TEMPLATES).map(([k, text]) => (
                        <button key={k} type="button" onClick={() => setReplyText(text)}
                          className="t-label px-3 py-1 border border-[#24272B] hover:!text-[#E8E6E1] capitalize"
                          data-testid={`deal-reply-template-${k}-${d.inquiry_id}`}>{k}</button>
                      ))}
                    </div>
                    <textarea className="input-solera" rows={4} placeholder={`Reply to ${d.name}. This is emailed to ${d.email}.`} value={replyText}
                      onChange={(e) => setReplyText(e.target.value)} data-testid={`deal-reply-text-${d.inquiry_id}`} />
                    <button className="btn-primary" disabled={busy || !replyText.trim()} onClick={() => replyDeal(d)} data-testid={`deal-reply-send-${d.inquiry_id}`}>
                      {busy ? "Sending" : "Send reply"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {deals.length === 0 && <p className="t-small text-muted-s">No deal submissions yet.</p>}
        </div>
      </section>

      <section data-testid="inbox-applications">
        <div className="flex items-baseline gap-4">
          <span className="t-label">Membership applications</span>
          <span className="t-cap text-muted-s fig">{apps.length}</span>
        </div>
        <div className="max-w-3xl space-y-0 mt-4">
          {apps.map((a) => {
            const key = `app:${a.application_id}`;
            const href = linkedinHref(a.linkedin);
            return (
              <div key={a.application_id} className="hairline py-5" data-testid={`application-${a.application_id}`}>
                <div className="flex gap-6 items-baseline flex-wrap">
                  <span className="t-body text-bone">{a.name}</span>
                  <a href={`mailto:${a.email}`} className="t-cap text-muted-s hover:!text-[#E8E6E1]">{a.email}</a>
                  {a.investor_type && <span className="t-label" data-testid={`app-type-${a.application_id}`}>{TYPE_LABEL[a.investor_type] || a.investor_type}</span>}
                  <span className="t-cap text-muted-s fig">{ts(a.created_at)}</span>
                  {a.reviewed && <span className="t-label text-tint" data-testid={`app-reviewed-${a.application_id}`}>Reviewed</span>}
                  {a.replied && <span className="t-label text-tint" data-testid={`app-replied-${a.application_id}`}>Replied</span>}
                  {a.invited && <span className="t-label text-tint" data-testid={`app-invited-${a.application_id}`}>Invited</span>}
                </div>
                <div className="flex gap-4 items-baseline flex-wrap mt-2">
                  {a.linkedin && (href ? (
                    <a href={href} target="_blank" rel="noreferrer"
                      className="t-label px-3 py-1 border border-[#24272B] hover:!text-[#E8E6E1]"
                      data-testid={`app-linkedin-${a.application_id}`}>LinkedIn &#8599;</a>
                  ) : (
                    <span className="t-cap text-muted-s break-all" data-testid={`app-linkedin-${a.application_id}`}>{a.linkedin}</span>
                  ))}
                  {a.referred_by && <span className="t-cap text-muted-s">Referred by {a.referred_by}</span>}
                </div>
                {a.message && <p className="t-small text-muted-s mt-2 measure">{a.message}</p>}
                <div className="flex gap-5 mt-3 flex-wrap">
                  <button onClick={() => toggleReply(key)} className="t-label hover:!text-[#E8E6E1]" data-testid={`app-reply-toggle-${a.application_id}`}>{replyFor === key ? "Cancel" : "Reply"}</button>
                  {!a.reviewed && (
                    <button onClick={() => reviewApp(a)} className="t-label hover:!text-[#E8E6E1]" data-testid={`app-review-${a.application_id}`}>Mark reviewed</button>
                  )}
                  {!a.invited && (
                    <button onClick={() => inviteApp(a)} disabled={busy} className="t-label text-tint hover:!text-[#E8E6E1]" data-testid={`app-invite-${a.application_id}`}>Approve &amp; invite</button>
                  )}
                </div>
                {replyFor === key && (
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2 flex-wrap" data-testid={`app-reply-templates-${a.application_id}`}>
                      {Object.entries(APP_TEMPLATES).map(([k, text]) => (
                        <button key={k} type="button" onClick={() => setReplyText(text)}
                          className="t-label px-3 py-1 border border-[#24272B] hover:!text-[#E8E6E1] capitalize"
                          data-testid={`app-reply-template-${k}-${a.application_id}`}>{k.replace("_", " ")}</button>
                      ))}
                    </div>
                    <textarea className="input-solera" rows={4} placeholder={`Reply to ${a.name}. This is emailed to ${a.email}.`} value={replyText}
                      onChange={(e) => setReplyText(e.target.value)} data-testid={`app-reply-text-${a.application_id}`} />
                    <button className="btn-primary" disabled={busy || !replyText.trim()} onClick={() => replyApp(a)} data-testid={`app-reply-send-${a.application_id}`}>
                      {busy ? "Sending" : "Send reply"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {apps.length === 0 && <p className="t-small text-muted-s">No applications yet.</p>}
        </div>
      </section>
    </div>
  );
}
